import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { Lines } from "@/components/marketing/Lines";
import { BODY_SMALL, HEADING_2 } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { BoldRuns, LandingSection } from "../LandingPrimitives";
import { sectionPad } from "./landingRun";

type FeatureList = Laid<"featureList">;
type Item = FeatureList["items"][number];

/** `.ppc-check-list_icon` and `.pps-trust-item-stars`: the same marks on every card. */
const TICK = "/marketing/campaign/feature-check.svg";
const STARS = "/marketing/campaign/trust-stars.svg";

/** `--colors--outline`, the rule the quote box and its divider are drawn in. */
const OUTLINE = "rgba(79, 92, 101, 0.21)";

/** `.ppc_features-list`: the tinted panel, its cards 64 apart, 32 below the wide step. */
const PANEL = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "64px",
  p: "64px 40px",
  borderRadius: "32px",
  backgroundColor: "rgba(242, 244, 255, 0.65)",
  [NOT_WIDE]: { gap: "32px", p: "40px 24px" },
  [TINY]: { p: "24px 16px" },
} satisfies SxProps<Theme>;

/** `.ppc_features-title_wrap`: the panel's heading, centred on 550. */
const TITLE_WRAP = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  maxWidth: "550px",
  textAlign: "center",
} satisfies SxProps<Theme>;

/**
 * `.ppc_features-item.is-autofit`: the white card, two 29rem columns while the
 * row holds them and one below that, 80 apart and 32 below the wide step.
 */
const CARD = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 29rem), 1fr))",
  gap: "80px",
  width: 1,
  p: "40px",
  borderRadius: "24px",
  backgroundColor: "common.white",
  [NOT_WIDE]: { gap: "32px", p: "24px" },
  [TINY]: { p: "16px" },
} satisfies SxProps<Theme>;

/** `.h3_new`, on the site's own heading tracking. */
const CARD_TITLE = {
  color: marketingPalette.ink,
  fontSize: 32,
  fontWeight: 500,
  lineHeight: 1.3,
  letterSpacing: "-0.04em",
  [TINY]: { fontSize: 24, lineHeight: "32px" },
} satisfies SxProps<Theme>;

/**
 * `.ppc_features-item_right`: the picture, 3:2 and cropped to fill. Stacked,
 * every card sets it first: the published grid gives the text-first cards'
 * pictures `order: -9999` below 992.
 */
const PICTURE = {
  display: "block",
  width: 1,
  height: 1,
  aspectRatio: "3 / 2",
  objectFit: "cover",
  borderRadius: "16px",
  [NOT_WIDE]: { order: -1 },
  [MID]: { borderRadius: "8px" },
} satisfies SxProps<Theme>;

/** `.body_new.is-13-px`. */
const SMALL_LINE = {
  fontSize: 12.96,
  lineHeight: "18.144px",
  textAlign: "left",
} satisfies SxProps<Theme>;

function Checks({ lines }: { lines: ReadonlyArray<string> }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {lines.map((line, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <Box
            component="img"
            src={TICK}
            alt=""
            loading="lazy"
            sx={{ flex: "none", width: "12px", height: "12px" }}
          />
          <Typography sx={BODY_SMALL}>{line}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** `.ppc_features-item_trust_box`: a customer's quote under its stars, signed under a rule. */
function Review({ review }: { review: NonNullable<Item["review"]> }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        p: "16px",
        borderRadius: "8px",
        border: `1px solid ${OUTLINE}`,
        backgroundColor: "rgba(224, 247, 228, 0.2)",
      }}
    >
      <Box
        component="img"
        src={STARS}
        alt=""
        loading="lazy"
        sx={{ display: "block", width: "64px" }}
      />
      {review.quote ? (
        <Typography sx={BODY_SMALL}>{review.quote}</Typography>
      ) : null}
      <Box sx={{ height: "0.96px", backgroundColor: OUTLINE }} />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {review.name ? (
          <Typography sx={SMALL_LINE}>{review.name}</Typography>
        ) : null}
        {review.source ? (
          <Typography sx={[SMALL_LINE, { opacity: 0.7 }]}>
            {review.source}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** One card: every other one sets its picture before its text, starting from the second. */
function FeatureCard({ item, artFirst }: { item: Item; artFirst: boolean }) {
  const picture = item.image ? (
    <Box component="img" src={item.image} alt="" loading="lazy" sx={PICTURE} />
  ) : null;
  return (
    <Box sx={CARD}>
      {artFirst ? picture : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "32px",
          [NOT_WIDE]: { gap: "24px" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography component="h3" sx={CARD_TITLE}>
            <Lines text={item.title} />
          </Typography>
          {item.paragraphs?.map((paragraph, index) => (
            <Typography key={index} sx={BODY_SMALL}>
              <BoldRuns text={paragraph} />
            </Typography>
          ))}
        </Box>
        {item.checks?.length ? <Checks lines={item.checks} /> : null}
        {item.review ? <Review review={item.review} /> : null}
      </Box>
      {artFirst ? null : picture}
    </Box>
  );
}

/**
 * `.ppc_features-list`: the campaign pages' reasons, one white card each on a
 * tinted panel, the text beside a picture and closing on its ticks or on a
 * customer's quote.
 */
export default function FeatureListBlock({ block }: { block: FeatureList }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "top"}>
      <Box sx={PANEL}>
        {block.title ? (
          <Box sx={TITLE_WRAP}>
            <Typography component="h2" sx={HEADING_2}>
              <Lines text={block.title} />
            </Typography>
            {block.body ? (
              <Typography sx={BODY_SMALL}>
                <BoldRuns text={block.body} />
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {block.items.map((item, index) => (
          <FeatureCard key={index} item={item} artFirst={index % 2 === 1} />
        ))}
      </Box>
    </LandingSection>
  );
}
